import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, ArrowRight, Clock } from 'lucide-react';

const FEATURE_LABELS = {
  technical_analysis: 'Technical Analysis',
  macro_calendar: 'Macro Dashboard',
  screener: 'Screener',
  community_posts: 'Community posts',
};

/**
 * UsageBar - Non-intrusive progress bar showing remaining daily uses.
 * Props: feature, used, limit
 */
export const UsageBar = ({ feature, used, limit }) => {
  const percentage = Math.min((used / limit) * 100, 100);
  const remaining = Math.max(limit - used, 0);
  const label = FEATURE_LABELS[feature] || feature;

  const barColor =
    percentage >= 100
      ? 'bg-red-400'
      : percentage >= 80
      ? 'bg-orange-400'
      : 'bg-emerald-400';

  return (
    <div className="bg-white/80 border border-gray-200 rounded-lg px-4 py-2.5 mb-4 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-1.5 text-sm text-gray-600">
          <Clock className="w-3.5 h-3.5" />
          <span>
            <span className="font-medium text-gray-900">{remaining}/{limit}</span>
            {' '}{label} remaining today
          </span>
        </div>
      </div>
      <div className="w-28 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * UsageLimitOverlay - Full-screen warm overlay when daily limit is reached.
 * Props: feature, limit
 */
export const UsageLimitOverlay = ({ feature, limit }) => {
  const navigate = useNavigate();
  const label = FEATURE_LABELS[feature] || feature;

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-4">
      <Card className="max-w-lg w-full border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-yellow-50">
        <CardContent className="pt-12 pb-8 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            You've been busy today!
          </h2>
          <p className="text-gray-600 mb-6">
            You've used all {limit} free {label} views for today.
            Limits reset at midnight UTC.
          </p>

          <div className="bg-white rounded-lg p-4 mb-6 border border-orange-200">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Sparkles className="w-5 h-5 text-orange-500" />
              <span className="font-semibold text-gray-900">Upgrade to PRO</span>
            </div>
            <p className="text-sm text-gray-600">
              Get unlimited access to all Era features starting at $4.99/mo.
            </p>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => navigate('/pricing')}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:opacity-90"
            >
              View Plans
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full text-gray-600"
            >
              Maybe Tomorrow
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsageBar;
